import React from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { NewTermsDialog } from '@/components/admin/terms/NewTermsDialog';
import { EditTermsDialog } from '@/components/admin/terms/EditTermsDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Pencil, Trash2, Eye } from 'lucide-react';

interface TermsData {
  id: number;
  version: string;
  content: string;
  published_at: string;
  is_current: boolean;
}

const AdminTerms = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isNewDialogOpen, setIsNewDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = React.useState(false);
  const [selectedTerms, setSelectedTerms] = React.useState<TermsData | null>(null);

  const { data: terms, isLoading, refetch } = useQuery({
    queryKey: ['terms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terms_of_service')
        .select('*')
        .order('published_at', { ascending: false });
        
      if (error) throw error;
      return data as TermsData[];
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('terms_of_service')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms'] });
      toast({
        title: "Success",
        description: "Terms of service deleted successfully."
      });
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete terms of service."
      });
      console.error("Delete error:", error);
    }
  });

  const setAsCurrent = async (id: number) => {
    const { error: updateError } = await supabase
      .from('terms_of_service')
      .update({ is_current: false })
      .neq('id', id);
      
    if (updateError) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update terms status."
      });
      return;
    }

    const { error } = await supabase
      .from('terms_of_service')
      .update({ is_current: true })
      .eq('id', id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not set as current version."
      });
      return;
    }

    toast({
      title: "Success",
      description: "Terms of service updated successfully."
    });
    refetch();
  };

  const handleEdit = (term: TermsData) => {
    setSelectedTerms(term);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (term: TermsData) => {
    setSelectedTerms(term);
    setIsDeleteDialogOpen(true);
  };

  const handleView = (term: TermsData) => {
    window.open(`/terms-of-service?version=${term.id}`, '_blank');
  };

  const confirmDelete = () => {
    if (selectedTerms) {
      if (selectedTerms.is_current) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Cannot delete the current terms of service version."
        });
        setIsDeleteDialogOpen(false);
        return;
      }
      deleteMutation.mutate(selectedTerms.id);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AdminLayout title="Terms of Service Management">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Terms of Service Versions</CardTitle>
          <Button onClick={() => setIsNewDialogOpen(true)}>Add New Version</Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {terms?.map((term) => (
                <TableRow key={term.id}>
                  <TableCell className="font-medium">{term.version}</TableCell>
                  <TableCell>{new Date(term.published_at).toLocaleDateString()}</TableCell>
                  <TableCell>{term.is_current ? 'Current' : 'Previous'}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTerms(term);
                        setIsViewDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(term)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {!term.is_current && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAsCurrent(term.id)}
                        >
                          Set as Current
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(term)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      <NewTermsDialog 
        open={isNewDialogOpen} 
        onOpenChange={setIsNewDialogOpen} 
        onSuccess={refetch} 
      />
      
      <EditTermsDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={refetch}
        terms={selectedTerms}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the terms of service version {selectedTerms?.version}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          {selectedTerms && (
            <>
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">Terms of Service</h2>
                <p className="text-sm text-muted-foreground">
                  Version {selectedTerms.version} - Published {new Date(selectedTerms.published_at).toLocaleDateString()}
                  {!selectedTerms.is_current && " (Archive)"}
                </p>
                <div className="prose max-w-none">
                  <div className="bg-muted p-6 rounded-lg terms-content" 
                    dangerouslySetInnerHTML={{ __html: selectedTerms.content }} 
                  />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTerms;
